'use client';

import * as React from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';

import { cn } from '../../lib/utils';

/**
 * Drop-in replacement for the shadcn/ui `Select` (see `select.tsx`).
 *
 * It exposes the exact same compound API
 * (`Select` / `SelectTrigger` / `SelectValue` / `SelectContent` / `SelectItem`)
 * and looks identical to the regular select input, but the dropdown is an
 * autocomplete: it renders a search box and filters the options as you type.
 *
 * Because the API matches, existing usages only need their import path swapped
 * from `@/components/ui/select` to `@/components/ui/autocomplete-select`.
 */

interface AutocompleteItem {
  value: string;
  label: React.ReactNode;
  text: string;
  disabled?: boolean;
}

interface SelectOption {
  value: string;
  label: React.ReactNode;
  disabled?: boolean;
}

interface SelectContextValue {
  value?: string;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
  open: boolean;
  setOpen: (open: boolean) => void;
  search: string;
  setSearch: (search: string) => void;
  items: AutocompleteItem[];
  triggerRef: React.RefObject<HTMLButtonElement>;
}

const SelectContext = React.createContext<SelectContextValue | null>(null);

function useSelectContext(component: string): SelectContextValue {
  const ctx = React.useContext(SelectContext);
  if (!ctx) {
    throw new Error(`<${component}> must be used within <Select>`);
  }
  return ctx;
}

/** Extract a plain-text representation of a node, used for search matching. */
function getNodeText(node: React.ReactNode): string {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(getNodeText).join('');
  if (React.isValidElement(node)) {
    return getNodeText((node.props as { children?: React.ReactNode }).children);
  }
  return '';
}

/** Recursively walk the children tree to collect every `<SelectItem>`. */
function collectItems(node: React.ReactNode, acc: AutocompleteItem[]): void {
  React.Children.forEach(node, (child) => {
    if (!React.isValidElement(child)) return;
    if (child.type === SelectItem) {
      const props = child.props as {
        value: string;
        children?: React.ReactNode;
        disabled?: boolean;
      };
      acc.push({
        value: props.value,
        label: props.children,
        text: getNodeText(props.children),
        disabled: props.disabled,
      });
      return;
    }
    const props = child.props as { children?: React.ReactNode };
    if (props && props.children) {
      collectItems(props.children, acc);
    }
  });
}

interface SelectProps {
  value?: string;
  // Method signature (not a property) so it is bivariant — this lets call-sites
  // pass narrower handlers like `(v: AssociationType) => void`, matching the
  // behaviour of the Radix `Select` this component replaces.
  onValueChange?(value: string): void;
  disabled?: boolean;
  className?: string;
  items?: SelectOption[];
  children?: React.ReactNode;
}

const Select = ({
  value,
  onValueChange,
  disabled,
  className,
  items: providedItems,
  children,
}: SelectProps) => {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const containerRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  const items = React.useMemo(() => {
    if (providedItems) {
      return providedItems.map((item) => {
        return {
          ...item,
          text: getNodeText(item.label),
        };
      });
    }

    const acc: AutocompleteItem[] = [];
    collectItems(children, acc);
    return acc;
  }, [children, providedItems]);

  // Close on outside click.
  React.useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <SelectContext.Provider
      value={{
        value,
        onValueChange,
        disabled,
        open,
        setOpen,
        search,
        setSearch,
        items,
        triggerRef,
      }}
    >
      <div ref={containerRef} className={cn('relative', className)}>
        {children}
      </div>
    </SelectContext.Provider>
  );
};
Select.displayName = 'Select';

const SelectTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, children, onClick, ...props }, ref) => {
  const ctx = useSelectContext('SelectTrigger');

  // Merge the forwarded ref with the context trigger ref (used for width).
  const setRefs = React.useCallback(
    (node: HTMLButtonElement | null) => {
      (
        ctx.triggerRef as React.MutableRefObject<HTMLButtonElement | null>
      ).current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref)
        (ref as React.MutableRefObject<HTMLButtonElement | null>).current =
          node;
    },
    [ctx.triggerRef, ref]
  );

  return (
    <button
      ref={setRefs}
      type="button"
      role="combobox"
      aria-expanded={ctx.open}
      disabled={ctx.disabled}
      onClick={(event) => {
        onClick?.(event);
        if (!ctx.disabled) ctx.setOpen(!ctx.open);
      }}
      className={cn(
        'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1',
        className
      )}
      {...props}
    >
      {children}
      <ChevronDown
        className={cn(
          'h-4 w-4 shrink-0 opacity-50 transition-transform',
          ctx.open && 'rotate-180'
        )}
      />
    </button>
  );
});
SelectTrigger.displayName = 'SelectTrigger';

interface SelectValueProps {
  placeholder?: React.ReactNode;
  className?: string;
}

const SelectValue = ({ placeholder, className }: SelectValueProps) => {
  const ctx = useSelectContext('SelectValue');
  const selected = ctx.items.find((item) => item.value === ctx.value);

  if (ctx.value !== undefined && ctx.value !== '' && selected) {
    return (
      <span className={cn('line-clamp-1', className)}>{selected.label}</span>
    );
  }

  return (
    <span className={cn('line-clamp-1 text-muted-foreground', className)}>
      {placeholder}
    </span>
  );
};
SelectValue.displayName = 'SelectValue';

interface SelectContentProps {
  className?: string;
  /** Children are ignored — items are collected from `<Select>` and rendered with search. */
  children?: React.ReactNode;
  searchPlaceholder?: string;
  emptyMessage?: string;
  position?: string;
}

const SelectContent = ({
  className,
  searchPlaceholder = 'Search...',
  emptyMessage = 'No results found.',
}: SelectContentProps) => {
  const ctx = useSelectContext('SelectContent');
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [width, setWidth] = React.useState<number>();

  const filtered = React.useMemo(() => {
    const q = ctx.search.trim().toLowerCase();
    if (!q) return ctx.items;
    return ctx.items.filter((item) => item.text.toLowerCase().includes(q));
  }, [ctx.items, ctx.search]);

  React.useEffect(() => {
    if (ctx.open) {
      setWidth(ctx.triggerRef.current?.offsetWidth);
      inputRef.current?.focus();
    }
  }, [ctx.open, ctx.triggerRef]);

  if (!ctx.open) return null;

  return (
    <div
      style={width ? { width } : undefined}
      className={cn(
        'absolute left-0 top-full z-50 mt-1 min-w-[8rem] overflow-hidden rounded-lg border border-slate-200 bg-white text-slate-900 shadow-xl outline-none animate-in fade-in-0 zoom-in-95',
        className
      )}
    >
      <div className="flex items-center border-b border-slate-100 bg-white px-3 py-2">
        <Search className="mr-2 h-4 w-4 shrink-0 text-slate-400" />
        <input
          ref={inputRef}
          type="text"
          placeholder={searchPlaceholder}
          value={ctx.search}
          onChange={(e) => ctx.setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              ctx.setOpen(false);
              ctx.setSearch('');
            }
          }}
          className="flex h-8 w-full rounded-md bg-transparent text-sm text-slate-800 outline-none placeholder:text-slate-400 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
        />
        {ctx.search && (
          <button
            type="button"
            onClick={() => {
              ctx.setSearch('');
              inputRef.current?.focus();
            }}
            className="rounded-full p-1 hover:bg-slate-100"
          >
            <X className="h-3 w-3 text-slate-400" />
          </button>
        )}
      </div>

      <div className="max-h-[300px] overflow-y-auto bg-white p-1">
        {filtered.length === 0 ? (
          <div className="py-6 text-center text-sm text-slate-500">
            {emptyMessage}
          </div>
        ) : (
          filtered.map((item) => {
            const isSelected = item.value === ctx.value;
            return (
              <button
                key={item.value}
                type="button"
                disabled={item.disabled}
                className={cn(
                  'relative flex w-full cursor-pointer select-none items-center rounded-md py-2 pl-8 pr-2 text-left text-sm leading-5 text-slate-700 outline-none hover:bg-slate-100 hover:text-slate-950 focus:bg-slate-100 focus:text-slate-950 disabled:pointer-events-none disabled:opacity-50',
                  isSelected && 'bg-slate-100 font-medium text-slate-950'
                )}
                onClick={() => {
                  if (item.disabled) return;
                  ctx.onValueChange?.(item.value);
                  ctx.setOpen(false);
                  ctx.setSearch('');
                }}
              >
                <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                  {isSelected && <Check className="h-4 w-4" />}
                </span>
                <span>{item.label}</span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};
SelectContent.displayName = 'SelectContent';

interface SelectItemProps {
  value: string;
  children?: React.ReactNode;
  disabled?: boolean;
  className?: string;
}

/**
 * Declarative item. It renders nothing itself — `<Select>` collects these to
 * build the (searchable) option list. Kept as a component so the JSX shape of
 * existing call-sites is unchanged.
 */
const SelectItem = (_props: SelectItemProps): React.ReactElement | null => null;
SelectItem.displayName = 'SelectItem';

// Passthrough exports for API compatibility (currently unused in the codebase).
const SelectGroup = ({ children }: { children?: React.ReactNode }) => (
  <>{children}</>
);
const SelectLabel = ({ children }: { children?: React.ReactNode }) => (
  <div className="py-1.5 pl-8 pr-2 text-sm font-semibold">{children}</div>
);
const SelectSeparator = () => <div className="-mx-1 my-1 h-px bg-muted" />;

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
};
